import { apiJson, type ApiFetchInit } from "../../api/client";

// Wire-shape from the embed backend's
// GET /api/admin/teams/:accountId/license-overview. See
// packages/backend/src/admin/admin.types.ts for the authoritative
// definition — duplicated here so the FE doesn't take a runtime
// dependency on the backend's TS files.

export interface LicenseOverviewUser {
  id: string | number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  avatar?: string | null;
  accountRole?: string | null;
}

export interface LicenseOverviewRow {
  user: LicenseOverviewUser;
  personalPlan: string | null;
  teamPlan: string | null;
  effectivePlan: string | null;
  hasLicense: boolean;
}

export interface LicenseOverviewResponse {
  accountId: string;
  accountName: string | null;
  teamPlan: string | null;
  /** Purchased seat cap. Drives the licence usage meter. */
  licenseQuantity: number;
  /** Active licence rows for this account. */
  numberOfUsedLicenses: number;
  rows: LicenseOverviewRow[];
  /** AHA-42877: server-side pagination — echoed back from request,
   *  plus the unpaginated total after the search filter is applied. */
  page: number;
  size: number;
  totalUsers: number;
}

export interface LicenseOverviewQuery {
  page?: number;
  size?: number;
  /** Case-insensitive email substring; sent only when non-empty. */
  search?: string;
}

export function getLicenseOverview(
  accountId: string,
  query: LicenseOverviewQuery = {},
  init: ApiFetchInit = {},
): Promise<LicenseOverviewResponse> {
  const qs = new URLSearchParams();
  if (query.page != null) qs.set("page", String(query.page));
  if (query.size != null) qs.set("size", String(query.size));
  if (query.search != null && query.search.trim().length > 0) {
    qs.set("search", query.search);
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiJson<LicenseOverviewResponse>(
    `/admin/teams/${encodeURIComponent(accountId)}/license-overview${suffix}`,
    init,
  );
}

// AHA-42877: admin-only role mutation. The accountRole `owner` triggers
// an ownership transfer upstream — the current owner is atomically
// demoted to admin in one swap. UI must confirm this destructive
// side-effect before calling.
export type AdminSettableRole = "owner" | "admin" | "editor" | "viewer";
export function updateMemberRole(
  accountId: string,
  userId: number,
  accountRole: AdminSettableRole,
  init: ApiFetchInit = {},
): Promise<{ ok: true }> {
  return apiJson<{ ok: true }>(
    `/admin/teams/${encodeURIComponent(accountId)}/user/${userId}/role`,
    {
      ...init,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...init.headers },
      body: JSON.stringify({ accountRole }),
    },
  );
}

export function grantLicense(
  accountId: string,
  userId: number,
  init: ApiFetchInit = {},
): Promise<unknown> {
  return apiJson(
    `/admin/teams/${encodeURIComponent(accountId)}/licenses/grant`,
    {
      ...init,
      method: "POST",
      headers: { "Content-Type": "application/json", ...init.headers },
      body: JSON.stringify({ userId }),
    },
  );
}

export function revokeLicense(
  accountId: string,
  userId: number,
  init: ApiFetchInit = {},
): Promise<unknown> {
  return apiJson(
    `/admin/teams/${encodeURIComponent(accountId)}/licenses/revoke`,
    {
      ...init,
      method: "POST",
      headers: { "Content-Type": "application/json", ...init.headers },
      body: JSON.stringify({ userId }),
    },
  );
}
